import React, { useState, useRef, useCallback, useEffect } from "react";
import ReactDOM from "react-dom";
import { Table } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { Snackbar, Alert } from "@mui/material";
import CreatableSelect from "react-select/creatable";
import {
    agentQuery,
    searchAdvancedWithPayload,
    dumpCandidatesToList,
    getFolders,
    getListsByFolder,
    createFolder,
    createList,
} from "../../Redux/ActionCreators/candidateAction";
import { SKILLS_VISIBLE_COUNT, formatDate, getDaysFromToday } from "./candidateHelpers";

// ── Helpers (mirrors CandidateTableBody internals) ──────────────────────────
const parseSkills = (skills) => {
    if (!skills) return [];
    if (typeof skills === "string") return skills.split(",").map((s) => s.trim()).filter(Boolean);
    return skills;
};

const SkillsCell = ({ skills = null, secondarySkills = null }) => {
    const primaryList = parseSkills(skills);
    const secondaryList = parseSkills(secondarySkills);
    const [popupPos, setPopupPos] = useState(null);
    const triggerRef = useRef(null);

    if (!primaryList.length && !secondaryList.length)
        return <span style={{ color: "#C3C3C3" }}>N/A</span>;
    const visible = primaryList.slice(0, SKILLS_VISIBLE_COUNT);
    const extra = [...primaryList.slice(SKILLS_VISIBLE_COUNT), ...secondaryList];

    const showPopup = () => {
        if (!triggerRef.current) return;
        const rect = triggerRef.current.getBoundingClientRect();
        setPopupPos({ top: rect.bottom + window.scrollY + 4, left: rect.left + window.scrollX });
    };

    const hidePopup = () => setPopupPos(null);

    return (
        <div className="d-flex flex-wrap" style={{ gap: "5px" }}>
            {visible.map((s, i) => (
                <p key={i} className="candidate-skills" style={{ background: "#d6e7ff", color: "#0048b7", padding: "2px 5px", borderRadius: "6px" }}>
                    {s}
                </p>
            ))}
            {extra.length > 0 && (
                <>
                    <p
                        ref={triggerRef}
                        className="candidate-skills more-skills"
                        style={{ background: "#d6e7ff", color: "#0048b7", padding: "2px 5px", borderRadius: "6px", cursor: "pointer", margin: 0 }}
                        onMouseEnter={showPopup}
                        onMouseLeave={hidePopup}
                    >
                        +{extra.length} more
                    </p>
                    {popupPos && ReactDOM.createPortal(
                        <div
                            className="skills-popup"
                            style={{
                                position: "absolute",
                                top: popupPos.top,
                                left: popupPos.left,
                                zIndex: 9999,
                                pointerEvents: "none",
                                background: "#fff",
                                border: "1px solid #ccc",
                                borderRadius: "4px",
                                padding: "4px",
                                height: "fit-content",
                                maxHeight: "200px",
                                overflowY: "auto",
                            }}
                        >
                            {extra.map((s, i) => (
                                <span className="popup-skill-tag" key={i}>{s}{i < extra.length - 1 && ", "}</span>
                            ))}
                        </div>,
                        document.body
                    )}
                </>
            )}
        </div>
    );
};

// ── Move-to-List Modal ───────────────────────────────────────────────────────
const modalSelectStyles = {
    control: (base, state) => ({
        ...base,
        boxShadow: state.isFocused ? "0 0 0 1px #111927" : "none",
        borderRadius: "8px",
        fontSize: "14px",
        minHeight: "40px",
        "&:hover": { borderColor: "#111927" },
    }),
    option: (base, state) => ({
        ...base,
        fontSize: "14px",
        color: state.isSelected ? "white" : "#111927",
    }),
    placeholder: (base) => ({ ...base, color: "#B0B8C1" }),
};

const labelStyle = {
    display: "block",
    fontSize: "14px",
    lineHeight: "21px",
    fontWeight: 600,
    color: "#111928",
    marginBottom: "6px",
};

const MoveToListModal = ({ selectedCount, onClose, onSuccess }) => {
    const dispatch = useDispatch();
    const [folderOptions, setFolderOptions] = useState([]);
    const [listOptions, setListOptions] = useState([]);
    const [selectedFolder, setSelectedFolder] = useState(null);
    const [selectedList, setSelectedList] = useState(null);
    const [folderLoading, setFolderLoading] = useState(false);
    const [listLoading, setListLoading] = useState(false);
    const [moving, setMoving] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });

    useEffect(() => {
        setFolderLoading(true);
        dispatch(getFolders()).then((res) => {
            setFolderLoading(false);
            if (res?.success && Array.isArray(res.data)) {
                setFolderOptions(res.data.map((f) => ({ value: f.id, label: f.name })));
            }
        });
    }, [dispatch]);

    useEffect(() => {
        if (!selectedFolder) {
            setListOptions([]);
            setSelectedList(null);
            return;
        }
        setListLoading(true);
        setSelectedList(null);
        dispatch(getListsByFolder(selectedFolder.value)).then((res) => {
            setListLoading(false);
            if (res?.success && Array.isArray(res.data)) {
                setListOptions(res.data.map((l) => ({ value: l.id, label: l.name })));
            } else {
                setListOptions([]);
            }
        });
    }, [dispatch, selectedFolder]);

    const handleCreateFolder = async (inputValue) => {
        const result = await dispatch(createFolder({ name: inputValue, is_active: true }));
        if (result?.success) {
            const newOpt = { value: result.data?.id || result.data, label: inputValue };
            setFolderOptions((prev) => [...prev, newOpt]);
            setSelectedFolder(newOpt);
            setSnackbar({ open: true, message: "Folder created successfully", severity: "success" });
        } else {
            setFolderLoading(true);
            dispatch(getFolders()).then((res) => {
                setFolderLoading(false);
                if (res?.success && Array.isArray(res.data)) {
                    setFolderOptions(res.data.map((f) => ({ value: f.id, label: f.name })));
                }
            });
            const errorMsg = result?.status === 400 ? "Folder name already exists" : (result?.message || "Folder creation failed");
            setSnackbar({ open: true, message: errorMsg, severity: "error" });
        }
    };

    const handleCreateList = async (inputValue) => {
        if (!selectedFolder) return;
        const result = await dispatch(createList({
            name: inputValue,
            type: "manual",
            is_active: true,
            folder_id: selectedFolder.value,
            rules: [],
        }));
        if (result?.success) {
            const newOpt = { value: result.data?.id || result.data, label: inputValue };
            setListOptions((prev) => [...prev, newOpt]);
            setSelectedList(newOpt);
            setSnackbar({ open: true, message: "List created successfully", severity: "success" });
        } else {
            setListLoading(true);
            dispatch(getListsByFolder(selectedFolder.value)).then((res) => {
                setListLoading(false);
                if (res?.success && Array.isArray(res.data)) {
                    setListOptions(res.data.map((l) => ({ value: l.id, label: l.name })));
                }
            });
            const errorMsg = result?.status === 400 ? "List name already exists" : (result?.message || "List creation failed");
            setSnackbar({ open: true, message: errorMsg, severity: "error" });
        }
    };

    const handleMove = async () => {
        if (!selectedList || moving) return;
        setMoving(true);
        const result = await onSuccess(selectedList.value);
        setMoving(false);
        if (!result) onClose();
    };

    const canMove = selectedFolder && selectedList;

    return (
        <div className="upload-modal">
            <div className="modal-content" style={{ maxWidth: "460px", width: "100%" }}>
                <div className="modal-head">
                    <div />
                    <h3>Move to List</h3>
                    <img
                        src="/images/close-icon.svg"
                        alt="close"
                        onClick={onClose}
                        style={{ cursor: "pointer" }}
                    />
                </div>

                <div style={{ padding: "0 20px 24px" }}>
                    <p style={{ fontSize: "14px", color: "#8A9199", marginBottom: "20px", textAlign: "center" }}>
                        Move <strong>{selectedCount}</strong> selected candidate{selectedCount !== 1 ? "s" : ""} to a list.
                    </p>

                    <div style={{ marginBottom: "18px" }}>
                        <label style={labelStyle}>Select Folder</label>
                        <CreatableSelect
                            className="mt-2"
                            isClearable
                            isLoading={folderLoading}
                            isDisabled={folderLoading}
                            options={folderOptions}
                            value={selectedFolder}
                            onChange={setSelectedFolder}
                            onCreateOption={handleCreateFolder}
                            styles={modalSelectStyles}
                            placeholder="Choose or create a folder…"
                            formatCreateLabel={(val) => `+ Create folder "${val}"`}
                        />
                    </div>

                    <div style={{ marginBottom: "24px" }}>
                        <label style={labelStyle}>Select List</label>
                        <CreatableSelect
                            className="mt-2"
                            isClearable
                            isLoading={listLoading}
                            isDisabled={!selectedFolder || listLoading}
                            options={listOptions}
                            value={selectedList}
                            onChange={setSelectedList}
                            onCreateOption={handleCreateList}
                            styles={modalSelectStyles}
                            placeholder={selectedFolder ? "Choose or create a list…" : "Select a folder first"}
                            formatCreateLabel={(val) => `+ Create list "${val}"`}
                        />
                    </div>

                    <div className="d-flex justify-content-end" style={{ gap: "12px" }}>
                        <button
                            className="upload-btn"
                            onClick={onClose}
                            disabled={moving}
                            style={{ border: "1px solid #111928", color: "#111928", backgroundColor: "transparent", cursor: moving ? "not-allowed" : "pointer" }}
                        >
                            Cancel
                        </button>
                        <button
                            className="upload-btn"
                            onClick={handleMove}
                            disabled={!canMove || moving}
                            style={{ opacity: !canMove || moving ? 0.5 : 1, cursor: !canMove || moving ? "not-allowed" : "pointer" }}
                        >
                            {moving ? "Moving…" : "Move to List"}
                        </button>
                    </div>
                </div>
            </div>

            <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
                <Alert onClose={() => setSnackbar((s) => ({ ...s, open: false }))} severity={snackbar.severity} sx={{ width: "100%" }}>{snackbar.message}</Alert>
            </Snackbar>
        </div>
    );
};

const LIST_VISIBLE_COUNT = 2;

const ListsCell = ({ lists = null }) => {
    const listArray = Array.isArray(lists) ? lists : (lists ? [lists] : []);
    const [popupPos, setPopupPos] = useState(null);
    const triggerRef = useRef(null);

    if (!listArray.length) return <span style={{ color: "#C3C3C3" }}>N/A</span>;

    const visible = listArray.slice(0, LIST_VISIBLE_COUNT);
    const extra = listArray.slice(LIST_VISIBLE_COUNT);

    const showPopup = () => {
        if (!triggerRef.current) return;
        const rect = triggerRef.current.getBoundingClientRect();
        setPopupPos({ top: rect.bottom + window.scrollY + 4, left: rect.left + window.scrollX });
    };

    const hidePopup = () => setPopupPos(null);

    return (
        <div className="d-flex flex-wrap" style={{ gap: "5px" }}>
            {visible.map((item, idx) => (
                <p className="candidate-skills" key={item.id || idx} style={{ background: '#e8f5e9', color: '#2e7d32', padding: '2px 5px', borderRadius: '6px', margin: 0 }}>
                    {item.name}
                </p>
            ))}
            {extra.length > 0 && (
                <>
                    <p
                        ref={triggerRef}
                        className="candidate-skills more-skills"
                        style={{ background: "#e8f5e9", color: "#2e7d32", padding: "2px 5px", borderRadius: "6px", cursor: "pointer", margin: 0 }}
                        onMouseEnter={showPopup}
                        onMouseLeave={hidePopup}
                    >
                        +{extra.length} more
                    </p>
                    {popupPos && ReactDOM.createPortal(
                        <div
                            className="skills-popup"
                            style={{
                                position: "absolute",
                                top: popupPos.top,
                                left: popupPos.left,
                                zIndex: 9999,
                                pointerEvents: "none",
                                background: "#fff",
                                border: "1px solid #ccc",
                                borderRadius: "4px",
                                padding: "4px",
                                height: "fit-content",
                                maxHeight: "200px",
                                overflowY: "auto",
                            }}
                        >
                            {extra.map((item, i) => (
                                <span className="popup-skill-tag" key={item.id || i}>{item.name}{i < extra.length - 1 && ", "}</span>
                            ))}
                        </div>,
                        document.body
                    )}
                </>
            )}
        </div>
    );
};

// ── Main Component ───────────────────────────────────────────────────────────
const SmartSearchTab = ({ onViewSummary, onDownload, onAddClick }) => {
    const dispatch = useDispatch();

    const [query, setQuery] = useState("");
    const [hasSearched, setHasSearched] = useState(false);
    const [searching, setSearching] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [candidates, setCandidates] = useState([]);
    const [inputPayload, setInputPayload] = useState(null);
    const [total, setTotal] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });

    // Checkbox state
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [showMoveModal, setShowMoveModal] = useState(false);

    const tableContainerRef = useRef(null);
    const loadingMoreRef = useRef(false);

    const handleSearch = async () => {
        const trimmed = query.trim();
        if (!trimmed || searching) return;

        setSearching(true);
        setCandidates([]);
        setInputPayload(null);
        setTotal(0);
        setCurrentPage(1);
        setHasSearched(false);
        setSelectedIds(new Set());

        const result = await dispatch(agentQuery(trimmed));
        setSearching(false);

        if (!result?.success) {
            setSnackbar({ open: true, message: result?.message || "Search failed. Please try again.", severity: "error" });
            return;
        }

        const { message, api_output } = result.data;

        if (message && !api_output) {
            setSnackbar({ open: true, message, severity: "info" });
            return;
        }

        if (api_output) {
            setHasSearched(true);
            setCandidates(api_output.candidates || []);
            setInputPayload(api_output.input_payload);
            setTotal(api_output.total || 0);
            setCurrentPage(api_output.page || 1);
            if (message) setSnackbar({ open: true, message, severity: "info" });
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter") handleSearch();
    };

    const loadMore = useCallback(async () => {
        if (loadingMoreRef.current || !inputPayload || candidates.length >= total) return;
        loadingMoreRef.current = true;
        setLoadingMore(true);

        const nextPage = currentPage + 1;
        const nextSkip = (nextPage - 1) * (inputPayload.limit || 10);
        const payload = { ...inputPayload, skip: nextSkip };

        const result = await dispatch(searchAdvancedWithPayload(payload));
        setLoadingMore(false);
        loadingMoreRef.current = false;

        if (result?.success && Array.isArray(result.data) && result.data.length > 0) {
            setCandidates((prev) => [...prev, ...result.data]);
            setCurrentPage(nextPage);
        }
    }, [dispatch, inputPayload, candidates.length, total, currentPage]);

    useEffect(() => {
        const container = tableContainerRef.current;
        if (!container) return;
        const handleScroll = () => {
            const { scrollTop, scrollHeight, clientHeight } = container;
            if (scrollHeight - scrollTop - clientHeight < 80) loadMore();
        };
        container.addEventListener("scroll", handleScroll);
        return () => container.removeEventListener("scroll", handleScroll);
    }, [loadMore]);

    // Checkbox handlers
    const allSelected = candidates.length > 0 && selectedIds.size === candidates.length;
    const someSelected = selectedIds.size > 0 && selectedIds.size < candidates.length;

    const toggleAll = () => {
        if (allSelected) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(candidates.map((c) => c.id)));
        }
    };

    const toggleOne = (id) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    // Move to list handler
    const handleMoveToList = async (list_id) => {
        const candidate_ids = Array.from(selectedIds);
        const result = await dispatch(dumpCandidatesToList(list_id, candidate_ids));
        if (result?.success) {
            setSnackbar({ open: true, message: result.message || "Candidates moved successfully.", severity: "success" });
            setSelectedIds(new Set());
            setShowMoveModal(false);
            return null;
        } else {
            setSnackbar({ open: true, message: result?.message || "Move failed. Please try again.", severity: "error" });
            setShowMoveModal(false);
            return "error";
        }
    };

    const closeSnackbar = () => setSnackbar((s) => ({ ...s, open: false }));

    // ── Initial (no results yet) ─────────────────────────────────────────────
    if (!hasSearched) {
        return (
            <div
                className="table-content"
                style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "40px 24px", backgroundColor: "#fff", overflowY: "auto" }}
            >
                <div style={{ marginBottom: "16px", textAlign: "center" }}>
                    <img src="/images/ai-search.svg" alt="" style={{ width: "48px", marginBottom: "12px" }} />
                    <h4 style={{ fontWeight: 700, color: "#111928", marginBottom: "6px" }}>
                        AI-Powered Candidate Search
                    </h4>
                    <p style={{ color: "#8A9199", fontSize: "14px", maxWidth: "440px", margin: "0 auto" }}>
                        Describe the candidate you're looking for in plain language.
                    </p>
                </div>

                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                        maxWidth: "620px",
                        height: "54px",
                        border: "2px solid #F75891",
                        borderRadius: "12px",
                        overflow: "hidden",
                        background: "#fff",
                        boxShadow: "0 4px 24px rgba(247,88,145,0.10)",
                    }}
                >
                    <span style={{ paddingLeft: "16px", display: "flex", alignItems: "center" }}>
                        <img src="/images/search.svg" alt="" style={{ width: "18px", opacity: 0.5 }} />
                    </span>
                    <input
                        type="text"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="e.g. I need 5 candidates with skill python..."
                        style={{ flex: 1, border: "none", outline: "none", padding: "16px 12px", fontSize: "15px", color: "#111928", background: "transparent" }}
                        autoFocus
                    />
                    <button
                        onClick={handleSearch}
                        disabled={!query.trim() || searching}
                        style={{
                            background: "#F75891", border: "none", color: "#fff",
                            padding: "0 24px", height: "54px", fontSize: "14px", fontWeight: 600,
                            cursor: !query.trim() || searching ? "not-allowed" : "pointer",
                            opacity: !query.trim() || searching ? 0.7 : 1,
                            whiteSpace: "nowrap", transition: "opacity 0.2s",
                        }}
                    >
                        {searching ? "Searching…" : "Search"}
                    </button>
                </div>

                <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={closeSnackbar} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
                    <Alert onClose={closeSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>{snackbar.message}</Alert>
                </Snackbar>
            </div>
        );
    }

    // ── Results view ─────────────────────────────────────────────────────────
    return (
        <div className="table-content">
            {/* Controls row */}
            <div className="controls" style={{ paddingBottom: "12px" }}>
                {/* Compact search bar */}
                <div
                    style={{
                        display: "flex", alignItems: "center",
                        border: "1.5px solid #E4E9F0", borderRadius: "8px",
                        overflow: "hidden", background: "#F7F9FC",
                        width: "100%", maxWidth: "480px", height: "40px",
                    }}
                >
                    <span style={{ paddingLeft: "12px", display: "flex", alignItems: "center" }}>
                        <img src="/images/search.svg" alt="" style={{ width: "16px", opacity: 0.4 }} />
                    </span>
                    <input
                        type="text"
                        value={query}
                        readOnly
                        placeholder="Refine your search…"
                        style={{ flex: 1, border: "none", outline: "none", padding: "10px 10px", fontSize: "13px", color: "#8A9199", background: "transparent", cursor: "default" }}
                    />
                    <button
                        onClick={() => {
                            setCandidates([]);
                            setInputPayload(null);
                            setTotal(0);
                            setCurrentPage(1);
                            setSelectedIds(new Set());
                            setHasSearched(false);
                            setQuery("");
                        }}
                        title="Clear search"
                        style={{
                            background: "none", border: "none", cursor: "pointer",
                            padding: "0 10px", height: "40px", display: "flex", alignItems: "center",
                            color: "#8A9199", fontSize: "18px", lineHeight: 1,
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.color = "#F75891")}
                        onMouseLeave={(e) => (e.currentTarget.style.color = "#8A9199")}
                    >
                        ×
                    </button>
                    <button
                        disabled
                        style={{
                            background: "#F75891", border: "none", color: "#fff",
                            padding: "0 16px", height: "40px", fontSize: "13px", fontWeight: 600,
                            cursor: "not-allowed", opacity: 0.5,
                        }}
                    >
                        Search
                    </button>
                </div>

                {/* Move to List button — only shown when at least one checkbox is checked */}
                <div className="filter-buttons">
                    <button
                        className="apply-assigned-btn"
                        onClick={() => setShowMoveModal(true)}
                        disabled={selectedIds.size === 0}
                        style={{
                            backgroundColor: selectedIds.size === 0 ? "" : "#F7FAFC",
                            width: "130px",
                            height: "32px",
                            borderRadius: "4px",
                            fontFamily: 'inter, sans-serif',
                            fontWeight: 400,
                            fontSize: "12px",
                            color: "#0B0C0D",
                            border: "1px solid #F75891",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: selectedIds.size === 0 ? "not-allowed" : "pointer",
                        }}
                        title={selectedIds.size === 0 ? "Select at least one candidate to move to list" : "Move to list"}
                    >
                        Move to List ({selectedIds.size})
                    </button>
                </div>
            </div>

            {/* Table */}
            <div ref={tableContainerRef} className="cv-ss-table-scroll">
                <Table className="job-table">
                    <thead>
                        <tr>
                            <th style={{ width: "40px" }}>
                                <input
                                    type="checkbox"
                                    checked={allSelected}
                                    ref={(el) => { if (el) el.indeterminate = someSelected; }}
                                    onChange={toggleAll}
                                    style={{ cursor: "pointer", width: "16px", height: "16px" }}
                                />
                            </th>
                            <th style={{ width: '120px' }}>Name</th>
                            <th className="text-center">Email</th>
                            <th className="text-center">Designation</th>
                            <th className="text-center">Added On</th>
                            <th>Skills</th>
                            <th className="text-center">List Name</th>
                            <th className="text-center">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {searching ? (
                            <tr>
                                <td colSpan="8">
                                    <div className="loading-overlay" style={{ alignItems: "start", color: "#F75891", height: "auto" }}>
                                        <div className="spinner-border" role="status">
                                            <span className="visually-hidden">Searching…</span>
                                        </div>
                                    </div>
                                </td>
                            </tr>
                        ) : candidates.length === 0 ? (
                            <tr>
                                <td colSpan="8" style={{ textAlign: "center", color: "#C3C3C3", fontSize: "14px" }}>
                                    No candidates found for your query.
                                </td>
                            </tr>
                        ) : (
                            candidates.map((c) => (
                                <tr key={c.id}>
                                    <td>
                                        <input
                                            type="checkbox"
                                            checked={selectedIds.has(c.id)}
                                            onChange={() => toggleOne(c.id)}
                                            style={{ cursor: "pointer", width: "16px", height: "16px" }}
                                        />
                                    </td>
                                    <td style={{ width: '120px' }}>
                                        <div className="d-flex flex-column gap-1">
                                            <strong style={{ cursor: "pointer" }}>{c.full_name}</strong>
                                            <p>{c.phone_number}</p>
                                        </div>
                                    </td>
                                    <td className="text-center">{c.email}</td>
                                    <td className="text-center">{c.current_designation || "N/A"}</td>
                                    <td className="text-center">
                                        <div className="d-flex flex-column gap-2">
                                            <strong>{getDaysFromToday(c.created_at)}</strong>
                                            <p>{formatDate(c.created_at)}</p>
                                        </div>
                                    </td>
                                    <td>
                                        <SkillsCell skills={c.primary_skills} secondarySkills={c.secondary_skills} />
                                    </td>
                                    <td className="text-center">
                                        {/* {c.list?.name || <p>N/A</p>} */}
                                        <ListsCell lists={c.lists} />
                                    </td>
                                    <td className="text-center">
                                        <div className="d-flex justify-content-center align-items-center gap-2">
                                            <button
                                                title="View Resume Summary"
                                                style={{ background: "none", border: "none", cursor: "pointer", padding: "0" }}
                                                onClick={() => onViewSummary(c)}
                                            >
                                                <img src="/images/eye-black-icon.svg" alt="View" width={20} height={20} />
                                            </button>
                                            <button
                                                title="Download Resume"
                                                style={{
                                                    background: "none", border: "none",
                                                    cursor: c.resume_url ? "pointer" : "not-allowed",
                                                    padding: "0", opacity: c.resume_url ? 1 : 0.4,
                                                }}
                                                disabled={!c.resume_url}
                                                onClick={() => onDownload(c)}
                                            >
                                                <img src="/images/download.svg" alt="Download" width={18} height={18} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </Table>

                {loadingMore && (
                    <div style={{ textAlign: "center", padding: "16px", color: "#F75891" }}>
                        <div className="spinner-border spinner-border-sm" role="status">
                            <span className="visually-hidden">Loading more…</span>
                        </div>
                        <span style={{ marginLeft: "8px", fontSize: "13px" }}>Loading more candidates…</span>
                    </div>
                )}
            </div>

            {/* Move to List Modal */}
            {showMoveModal && (
                <MoveToListModal
                    selectedCount={selectedIds.size}
                    onClose={() => setShowMoveModal(false)}
                    onSuccess={handleMoveToList}
                />
            )}

            <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={closeSnackbar} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
                <Alert onClose={closeSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>{snackbar.message}</Alert>
            </Snackbar>
        </div>
    );
};

export default SmartSearchTab;
